import { toast } from 'react-toastify';
import {
   ADD_STOCK_APPROVEL,
   APPROVEL_STATUS,
   BACKUP_DATABASE_ERROR,
   BACKUP_DATABASE_PROGRESS,
   BACKUP_DATABASE_START,
   BACKUP_DATABASE_SUCCESS,
   DEACTIVATE_DEALER_CHAT,
   DELETE_DEALER_CHAT,
   GENERATE_BULK_EWAY_EINVOICE,
   GET_EWAYBILL_AND_EINVOICE,
   GET_RECEIVE_APPROVAL,
   GET_SYNC_APPROVAL,
   POST_DELETE_UNUSED_MASTER,
   UPDATE_RECEIVED_REQUEST,
} from '../types';
import { POST_MERGE_CODE, POST_COLUMNS_HIDE_SHOW } from '../types';
import axios from '../../axios';

export const postMergeCode = (payload) => async (dispatch) => {
   try {
      const res = await axios.post('/setupFormat/merge-code', payload);

      if (res.data.code === 1) {
         dispatch({ type: POST_MERGE_CODE, payload: res.data.data });
         setTimeout(() => {
            toast.success('Code Is Merge successfully!');
         }, 1000);
      }
   } catch (err) {
      if (err.response?.status === 500) {
         toast.error(err.response.message);
         return;
      }
      if (err.response?.data?.message) {
         toast.error(err.response.data.message);
      }
   }
};

export const postDeleteUnusedMaster = (payload) => async (dispatch) => {
   try {
      const res = await axios.post('/setupFormat/delete-unused-master', payload);

      if (res.data.code === 1) {
         dispatch({ type: POST_DELETE_UNUSED_MASTER, payload: res.data.data });
         setTimeout(() => {
            toast.success('Unused mAsters Deleted successfully!');
         }, 1000);
      }
   } catch (err) {
      if (err.response?.status === 500) {
         toast.error(err.response.message);
         return;
      }
      if (err.response?.data?.message) {
         toast.error(err.response.data.message);
      }
   }
};

export const postColumnsHideShow = (payload) => async (dispatch) => {
   try {
      const res = await axios.post('/user/update/viewJson', payload);

      if (res.data.code === 1) {
         dispatch({ type: POST_COLUMNS_HIDE_SHOW, payload: res.data });
         setTimeout(() => {
            toast.success('Columns Save successfully!');
         }, 1000);
      }
   } catch (err) {
      if (err.response?.status === 500) {
         toast.error(err.response.message);
         return;
      }
      if (err.response?.data?.message) {
         toast.error(err.response.data.message);
      }
   }
};

export const backupDatabase = (payload) => async (dispatch) => {
   let downloadStarted = false;
   let controller = null;
   let timeoutId = null;

   try {
      dispatch({
         type: BACKUP_DATABASE_START,
         payload: { loading: true },
      });

      controller = new AbortController();

      // Increase timeout to 10 minutes for large databases
      timeoutId = setTimeout(() => {
         if (controller && !downloadStarted) {
            controller.abort();
         }
      }, 600000); // 10 minutes

      const res = await axios.post('/setupFormat/backup', payload, {
         responseType: 'blob',
         headers: {
            'Content-Type': 'application/json',
         },
         signal: controller.signal,
         timeout: 600000, // 10 minutes axios timeout
         onDownloadProgress: (progressEvent) => {
            if (!downloadStarted && progressEvent.loaded > 0) {
               downloadStarted = true;
               if (timeoutId) {
                  clearTimeout(timeoutId);
                  timeoutId = null;
               }
            }

            if (progressEvent.total) {
               const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

               // Dispatch progress update
               dispatch({
                  type: BACKUP_DATABASE_PROGRESS,
                  payload: {
                     progress: percentCompleted,
                     loaded: progressEvent.loaded,
                     total: progressEvent.total,
                  },
               });
            }
         },
      });

      // Clear timeout if still active
      if (timeoutId) {
         clearTimeout(timeoutId);
         timeoutId = null;
      }

      // Validate response status
      if (res.status !== 200) {
         throw new Error(`Server returned status ${res.status}`);
      }

      // Check if we got an error response disguised as a blob
      if (res.data.type === 'application/json' || res.data.size < 100) {
         try {
            const text = await res.data.text();
            const errorData = JSON.parse(text);
            throw new Error(errorData.message || 'Server error occurred');
         } catch (parseError) {
            // If it's not JSON and size is reasonable, continue with file download
            if (res.data.size < 100) {
               throw new Error('Invalid backup file received from server');
            }
         }
      }

      // Validate response data
      if (!res.data || res.data.size === 0) {
         throw new Error('No data received from server');
      }

      // Ensure we have a valid blob
      let blob;
      if (res.data instanceof Blob) {
         blob = res.data;
      } else {
         blob = new Blob([res.data], { type: 'application/octet-stream' });
      }

      // Get filename from headers
      const contentDisposition = res.headers['content-disposition'];
      let filename = `database_backup_${new Date().toISOString().replace(/[:.]/g, '-')}.sql`;

      if (contentDisposition) {
         const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
         if (filenameMatch) {
            filename = filenameMatch[1].replace(/['"]/g, '');
         }
      }

      // Create download URL
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create and configure download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';

      // Add to DOM and trigger download
      document.body.appendChild(link);

      // Use setTimeout to ensure the link is properly added to DOM
      setTimeout(() => {
         link.click();

         // Cleanup after download
         setTimeout(() => {
            if (document.body.contains(link)) {
               document.body.removeChild(link);
            }
            window.URL.revokeObjectURL(downloadUrl);
         }, 1000); // Increased cleanup delay
      }, 10);

      const fileSizeMB = (blob.size / (1024 * 1024)).toFixed(2);

      dispatch({
         type: BACKUP_DATABASE_SUCCESS,
         payload: {
            success: true,
            message: 'Database backup downloaded successfully',
            filename: filename,
            size: blob.size,
            sizeFormatted: `${fileSizeMB} MB`,
            timestamp: new Date().toISOString(),
         },
      });

      toast.success(`Database backup downloaded successfully (${fileSizeMB} MB)`);
   } catch (err) {
      console.error('Backup error:', err);

      // Cleanup timeouts
      if (timeoutId) {
         clearTimeout(timeoutId);
      }

      let errorMessage = 'Failed to download database backup';

      // Handle specific error types
      if (err.name === 'AbortError') {
         errorMessage = 'Backup request was cancelled or timed out';
      } else if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
         errorMessage = 'Connection timeout. Please check your network and try again.';
      } else if (err.response?.status === 500) {
         errorMessage = err.response.data?.message || 'Server error occurred';
      } else if (err.response?.status === 400) {
         errorMessage = err.response.data?.message || 'Bad request';
      } else if (err.response?.status === 404) {
         errorMessage = 'Backup service not found. Please contact support.';
      } else if (err.response?.status === 413) {
         errorMessage = 'Backup file is too large. Please contact support.';
      } else if (err.response?.status === 503) {
         errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (err.message) {
         errorMessage = err.message;
      }

      dispatch({
         type: BACKUP_DATABASE_ERROR,
         payload: {
            success: false,
            error: errorMessage,
            statusCode: err.response?.status,
         },
      });

      toast.error(errorMessage);
   }
};

// Additional helper function for manual cleanup if needed
export const cleanupBackupResources = () => {
   // Clean up any remaining blob URLs
   const links = document.querySelectorAll('a[href^="blob:"]');
   links.forEach((link) => {
      if (link.href.includes('blob:')) {
         window.URL.revokeObjectURL(link.href);
         if (document.body.contains(link)) {
            document.body.removeChild(link);
         }
      }
   });
};

export const getEwayBillandEinvoice = (formData) => async (dispatch) => {
   try {
      const res = await axios.get(`/setupFormat/e-way-bill/all`, {
         params: formData,
      });

      if (res.data.code === 1) {
         dispatch({
            type: GET_EWAYBILL_AND_EINVOICE,
            payload: res.data.data,
         });

         return res.data;
      }

      if (res.data.code === 2) {
         setTimeout(() => {
            toast.warn(res.data.message);
         }, 1000);
         return res.data;
      }

      return res.data;
   } catch (err) {
      const errorMessage = err.response?.data?.message || 'Something went wrong';
      toast.error(errorMessage);
      return err.response?.data || { error: true };
   }
};

export const generateBulkEwayEinvoice = (payload) => async (dispatch) => {
   try {
      const res = await axios.post('/setupFormat/bulk-eway-einvoice', payload);

      if (res.data.code === 1) {
         dispatch({ type: GENERATE_BULK_EWAY_EINVOICE, payload: res.data });

         setTimeout(() => {
            toast.success('Invoice generated successfully!');
         }, 1000);
      }
      if (res.data.code === 2) {
         dispatch({ type: GENERATE_BULK_EWAY_EINVOICE, payload: res.data });

         if (res.data.code === 2) {
            setTimeout(() => {
               toast.warn(res.data.message);
            }, 1000);
            setTimeout(() => {
               toast.error(res.data.data.failed[0].error);
            }, 1000);
            return res.data;
         }
      }
   } catch (err) {
      if (err.response?.status === 500) {
         toast.error(err.response.message);
         return;
      }
      if (err.response?.data?.message) {
         toast.error(err.response.data.message);
      }
   }
};

export const addStockApprovel = (payload) => async (dispatch) => {
   try {
      const res = await axios.post('/stock-approval/add/multi', payload);

      if (res.data.code === 200) {
         dispatch({ type: ADD_STOCK_APPROVEL, payload: res.data.data });
         setTimeout(() => {
            toast.success('Add successfully!');
         }, 1000);
      } else if (res.data.code === 400) {
         const failedReasons = res.data.data?.failed?.map(f => f.reason).join(', ');
         toast.error(failedReasons || res.data.message || 'Something went wrong.');
      }

   } catch (err) {
      if (err.response?.data?.message) {
         toast.error(err.response.data.message);
      }
   }
};

export const getsyncApproval = (formData) => async (dispatch) => {
   try {
      const res = await axios.get(`/stock-approval/get-requests`, {
         params: formData,
      });

      if (res.data.statusCode === 200 || res.data.code === 200) {
         dispatch({
            type: GET_SYNC_APPROVAL,
            payload: res.data.data,
         });
         return res.data.data;
      }

      if (res.data.statusCode === 2) {
         setTimeout(() => {
            toast.warn(res.data.message);
         }, 1000);
         return res.data.data;
      }

      return res.data.data;
   } catch (err) {
      const errorMessage = err.response?.data?.message || 'Something went wrong';
      toast.error(errorMessage);
      return err.response?.data || { error: true };
   }
};

export const getreceiveApprovel = (formData) => async (dispatch) => {
   try {
      const res = await axios.get(`/stock-approval/received-requests`, {
         params: formData,
      });

      if (res.data.statusCode === 200 || res.data.code === 200) {
         dispatch({
            type: GET_RECEIVE_APPROVAL,
            payload: res.data.data,
         });
         return res.data.data;
      }

      if (res.data.statusCode === 2) {
         setTimeout(() => {
            toast.warn(res.data.message);
         }, 1000);
         return res.data.data;
      }

      return res.data.data;
   } catch (err) {
      const errorMessage = err.response?.data?.message || 'Something went wrong';
      toast.error(errorMessage);
      return err.response?.data || { error: true };
   }
};

export const updateReceivedRequest = (payload) => async (dispatch) => {
   try {
      const res = await axios.post('/stock-approval/update-received', payload);

      if (res.data.code === 200) {
         dispatch({ type: UPDATE_RECEIVED_REQUEST, payload: res.data.data });
         toast.success('Updated successfully!');
         return res.data.data;
      }
   } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong');
   }
};

export const approvalStatus = (payload) => async (dispatch) => {
   try {
      const res = await axios.post('/stock-approval/approve', payload);

      if (res.data.code === 200) {
         dispatch({ type: APPROVEL_STATUS, payload: res.data.data });
         toast.success(payload.isApprove ? 'Approved!' : 'Unapproved!');
         return res.data.data;
      }
   } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong');
   }
};

// Delete action
export const deleteDealerChat = (id) => async (dispatch) => {
   try {
      const res = await axios.delete(`/stock-approval/delete/${id}`);

      if (res.data.code === 200) {
         dispatch({ type: DELETE_DEALER_CHAT, payload: id });
         toast.success('Record deleted successfully!');
         return res.data.data;
      }
   } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong');
   }
};

export const deactivateDealerChat = (id) => async (dispatch) => {
   try {
      const res = await axios.post(`/stock-approval/deactivate/${id}`);
      if (res.data.code === 200) {
         dispatch({ type: DEACTIVATE_DEALER_CHAT, payload: id });
         toast.success('Record deactivated successfully!');
         return res.data.data;
      }
   } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong');
   }
};